/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  Input,
  Select,
  Button,
  Text,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  FormHelperText,
} from "@chakra-ui/react";
import { FiPlus, FiRefreshCw } from "react-icons/fi";
import PotansiyelMusteriContextMenu from "components/ContextMenu/PotansiyelMusteriContextMenu";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";

export default function PotansiyelMusteri() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [durumFilter, setDurumFilter] = useState("aktif");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextMenuPos, setContextMenuPos] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const [saving, setSaving] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [newLead, setNewLead] = useState({
    firma_adi: "",
    telefon: "",
    email: "",
    arac_sayisi: "",
    cozum_turu: "",
    notlar: "",
  });
  const [editLead, setEditLead] = useState({
    id: null,
    firma_adi: "",
    telefon: "",
    email: "",
    arac_sayisi: "",
    cozum_turu: "",
    notlar: "",
    arama_tarihi: "",
  });
  const [stageModal, setStageModal] = useState({
    isOpen: false,
    stage: null,
    row: null,
    tarih: "",
    not: "",
  });
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    items: [],
    row: null,
  });
  const [expandedId, setExpandedId] = useState(null);
  const [endModal, setEndModal] = useState({
    isOpen: false,
    row: null,
    not: "",
  });
  const [smsModal, setSmsModal] = useState({
    isOpen: false,
    row: null,
    message: "",
  });
  const [teklifModal, setTeklifModal] = useState({
    isOpen: false,
    row: null,
    musteri_adi: "",
    email: "",
    talep_icerigi: "",
    fiyat: "",
    kalemler: [
      { urun_hizmet: "", adet: 1, birim_fiyat: "", toplam_fiyat: "" },
    ],
  });
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsTemplatesLoading, setSmsTemplatesLoading] = useState(false);
  const [smsTemplatesError, setSmsTemplatesError] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("q", debouncedSearch);
      if (durumFilter && durumFilter !== "tum") params.append("durum", durumFilter);

      const query = params.toString();
      const res = await api.get(`/api/crm/potansiyel-musteri${query ? `?${query}` : ""}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Veriler alınamadı.");
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = handleError(err, "Potansiyel müşteri verileri yükleme hatası");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, durumFilter, handleError]);

  const fetchSmsTemplates = useCallback(async () => {
    setSmsTemplatesLoading(true);
    setSmsTemplatesError(null);
    try {
      const res = await api.get("/api/abonelikler/sms/saved");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SMS şablonları alınamadı.");
      }
      setSmsTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = handleError(err, "SMS şablonları yükleme hatası");
      setSmsTemplatesError(errorMessage);
    } finally {
      setSmsTemplatesLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("tr-TR");
  };

  const tableData = useMemo(() => {
    const today = new Date();

    const diffInDays = (dateStr) => {
      if (!dateStr) return null;
      const target = new Date(dateStr);
      if (Number.isNaN(target.getTime())) return null;
      const diffMs = target.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0);
      return Math.round(diffMs / (1000 * 60 * 60 * 24));
    };

    return items.map((item) => {
      const gunFark = diffInDays(item.sonraki_arama_tarihi);
      let uyariNode = "-";
      if (gunFark === 1) {
        uyariNode = (
          <Text as="span" fontSize="lg" fontWeight="extrabold" color="red.400">
            !
          </Text>
        );
      } else if (gunFark === 0) {
        uyariNode = (
          <Text as="span" fontSize="xl" fontWeight="extrabold" color="red.600">
            !!!
          </Text>
        );
      }

      return {
        id: item.id,
        telefon: item.telefon || "-",
        firma_adi: item.firma_adi || "-",
        arac_sayisi: item.arac_sayisi ?? "-",
        email: item.email || "-",
        cozum_turu: item.cozum_turu || "-",
        arama_tarihi: formatDate(item.son_iletisim),
        surec_durumu: item.surec_durumu || "-",
        arama_asamasi: item.arama_asamasi ?? 0,
        sonraki_arama: formatDate(item.sonraki_arama_tarihi),
        uyari: uyariNode,
        raw: item,
      };
    });
  }, [items]);

  const closeContextMenu = () => {
    setContextMenuPos(null);
    setSelectedRow(null);
  };

  const handleRowContextMenu = (event, row) => {
    event.preventDefault();
    setSelectedRow(row);
    setContextMenuPos({ x: event.clientX, y: event.clientY });
  };

  const openStageModal = (row) => {
    const raw = row.raw || {};
    const currentStage = raw.arama_asamasi ?? 0;
    // Sonraki aşama: 1,2,3... (3'ten sonrası da 3 olarak kalabilir)
    const nextStage = Math.min((currentStage || 0) + 1, 3);
    setStageModal({
      isOpen: true,
      stage: nextStage,
      row,
      tarih: "",
      not: "",
    });
  };

  const closeStageModal = () => {
    setStageModal((prev) => ({ ...prev, isOpen: false, row: null }));
  };

  const handleStageSave = async () => {
    const { row, stage, tarih, not } = stageModal;
    if (!row || !stage) {
      closeStageModal();
      return;
    }
    try {
      const payload = {
        asama: stage,
        surec_durumu: "Devam",
        not_metni: not || null,
        sonraki_arama_tarihi: tarih || null,
      };
      const res = await api.post(
        `/api/crm/potansiyel-musteri/${row.id}/durum`,
        payload
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Dönüş güncellenemedi.");
      }
      toast({
        title: `${stage}. dönüş kaydedildi`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      closeStageModal();
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Potansiyel müşteri işlemi hatası");
    }
  };

  const openHistoryModal = (row) => {
    const raw = row.raw || {};
    const notlar = raw.notlar || "";
    const lines = notlar
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const items = lines.map((line, idx) => {
      let type = "Not";
      let label = line;
      if (line.startsWith("[DÖNÜŞ")) {
        type = "Dönüş";
        label = line;
      } else if (line.startsWith("[DURUM")) {
        type = "Durum";
        label = line;
      }
      return { id: idx, type, label };
    });

    setHistoryModal({
      isOpen: true,
      items,
      row,
    });
  };

  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, items: [], row: null });
  };

  const openEndModal = (row) => {
    setEndModal({
      isOpen: true,
      row,
      not: "",
    });
  };

  const closeEndModal = () => {
    setEndModal((prev) => ({ ...prev, isOpen: false, row: null, not: "" }));
  };

  const handleEndProcess = (row) => {
    openEndModal(row);
  };

  const handleEndSave = async () => {
    const { row, not } = endModal;
    if (!row) {
      closeEndModal();
      return;
    }
    try {
      const res = await api.post(
        `/api/crm/potansiyel-musteri/${row.id}/durum`,
        {
          surec_durumu: "sonlandirildi",
          durum: "pasif",
          not_metni: not || null,
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Süreç sonlandırılamadı.");
      }
      toast({
        title: "Süreç sonlandırıldı",
        status: "info",
      });
      closeEndModal();
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Potansiyel müşteri işlemi hatası");
    }
  };

  const openSmsModal = (row) => {
    if (!smsTemplates.length && !smsTemplatesLoading) {
      fetchSmsTemplates();
    }
    setSmsModal({
      isOpen: true,
      row,
      message: "",
    });
  };

  const closeSmsModal = () => {
    setSmsModal((prev) => ({ ...prev, isOpen: false, row: null, message: "" }));
  };

  const handleSmsSave = async () => {
    const { row, message } = smsModal;
    if (!row) {
      closeSmsModal();
      return;
    }
    if (!message.trim()) {
      toast({
        title: "Mesaj metni gerekli",
        status: "warning",
      });
      return;
    }
    try {
      const res = await api.post(
        `/api/crm/potansiyel-musteri/${row.id}/sms`,
        { message }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SMS gönderilemedi.");
      }
      toast({
        title: "SMS gönderildi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      closeSmsModal();
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SMS gönderim hatası");
    }
  };

  const openTeklifModal = (row) => {
    const raw = row.raw || {};
    setTeklifModal({
      isOpen: true,
      row,
      musteri_adi: raw.firma_adi || "",
      email: raw.email || "",
      talep_icerigi: raw.talep_icerigi || "",
      fiyat: "",
      kalemler: [
        { urun_hizmet: "", adet: 1, birim_fiyat: "", toplam_fiyat: "" },
      ],
    });
  };

  const closeTeklifModal = () => {
    setTeklifModal({
      isOpen: false,
      row: null,
      musteri_adi: "",
      email: "",
      talep_icerigi: "",
      fiyat: "",
      kalemler: [
        { urun_hizmet: "", adet: 1, birim_fiyat: "", toplam_fiyat: "" },
      ],
    });
  };

  const handleTeklifSave = async () => {
    const { row, musteri_adi, email, talep_icerigi, fiyat, kalemler } = teklifModal;
    if (!row) {
      closeTeklifModal();
      return;
    }
    // Kalemlerden toplam fiyatı hesapla (veya manuel girilen fiyatı kullan)
    let computedTotal = 0;
    kalemler.forEach((k) => {
      const qty = Number(k.adet) || 0;
      const unit = parseFloat(String(k.birim_fiyat || "").replace(",", ".")) || 0;
      const lineTotal =
        k.toplam_fiyat && String(k.toplam_fiyat).trim() !== ""
          ? parseFloat(String(k.toplam_fiyat).replace(",", "."))
          : qty * unit;
      if (!Number.isNaN(lineTotal)) {
        computedTotal += lineTotal;
      }
    });

    let price = parseFloat(String(fiyat || "").replace(",", "."));
    if (Number.isNaN(price) || price <= 0) {
      price = computedTotal;
    }

    if (Number.isNaN(price) || price <= 0) {
      toast({
        title: "Geçerli bir fiyat girin",
        status: "warning",
      });
      return;
    }
    // En az bir kalem dolu mu kontrol et
    const hasAnyItem = kalemler.some(
      (k) =>
        (k.urun_hizmet && k.urun_hizmet.trim() !== "") ||
        (k.birim_fiyat && String(k.birim_fiyat).trim() !== "")
    );
    if (!hasAnyItem) {
      toast({
        title: "En az bir kalem girmelisiniz",
        status: "warning",
      });
      return;
    }

    // Kalemleri tablo HTML satırlarına çevir
    const rowsHtml = kalemler
      .filter(
        (k) =>
          k.urun_hizmet && k.urun_hizmet.trim() !== "" && Number(k.adet) > 0
      )
      .map((k) => {
        const qty = Number(k.adet) || 0;
        const unit = parseFloat(String(k.birim_fiyat || "").replace(",", ".")) || 0;
        const lineTotal =
          k.toplam_fiyat && String(k.toplam_fiyat).trim() !== ""
            ? parseFloat(String(k.toplam_fiyat).replace(",", "."))
            : qty * unit;

        const birimStr =
          unit > 0 ? `${unit.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL` : "";
        const totalStr =
          lineTotal > 0
            ? `${lineTotal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL`
            : "";

        return `
          <tr>
            <td>${(k.urun_hizmet || "").trim()}</td>
            <td class="number">${qty}</td>
            <td class="number">${birimStr}</td>
            <td class="number">${totalStr}</td>
          </tr>
        `;
      })
      .join("");

    try {
      const talepIcerigiSafe = (talep_icerigi || "").trim();
      const payload = {
        musteri_adi: musteri_adi || null,
        email: email || null,
        fiyat: price,
        talep_icerigi: talepIcerigiSafe || null,
        kapsam_satirlari_html: rowsHtml || null,
      };
      const res = await api.post(
        `/api/crm/potansiyel-musteri/${row.id}/teklif-mail`,
        payload
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Teklif maili gönderilemedi.");
      }
      toast({
        title: "PDF teklif mail ile gönderildi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      closeTeklifModal();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Teklif mail gönderim hatası");
    }
  };

  const handleEdit = (row) => {
    const raw = row.raw || {};
    setEditLead({
      id: raw.id,
      firma_adi: raw.firma_adi || "",
      telefon: raw.telefon || "",
      email: raw.email || "",
      arac_sayisi:
        raw.arac_sayisi !== null && raw.arac_sayisi !== undefined
          ? String(raw.arac_sayisi)
          : "",
      cozum_turu: raw.cozum_turu || "",
      notlar: raw.notlar || "",
      arama_tarihi: raw.son_iletisim || "",
    });
    editModal.onOpen();
  };

  const filterControls = (
    <HStack spacing={3} flexWrap="wrap">
      <Input
        size="sm"
        placeholder="Ara..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        bg="white"
        width={{ base: "100%", md: "220px" }}
        fontSize="12px"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        _hover={{ borderColor: "gray.300" }}
        _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
      />
      <Select
        size="sm"
        value={durumFilter}
        onChange={(e) => setDurumFilter(e.target.value)}
        width={{ base: "100%", md: "160px" }}
        bg="white"
        borderColor="gray.200"
        color="gray.700"
        fontSize="12px"
        borderRadius="md"
        _hover={{ borderColor: "gray.300" }}
        _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
      >
        <option value="tum">Tümü</option>
        <option value="aktif">Aktif</option>
        <option value="pasif">Pasif</option>
      </Select>
      <Button
        size="sm"
        variant="outline"
        bg="white"
        color="gray.700"
        borderColor="gray.300"
        leftIcon={<FiRefreshCw />}
        fontSize="12px"
        fontWeight="medium"
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
        _active={{ bg: "gray.100" }}
        onClick={fetchItems}
      >
        Yenile
      </Button>
      <Button
        size="sm"
        bg="white"
        color="gray.700"
        borderColor="gray.300"
        border="1px solid"
        leftIcon={<FiPlus />}
        fontSize="12px"
        fontWeight="600"
        borderRadius="md"
        boxShadow="sm"
        _hover={{ 
          transform: "translateY(-1px)", 
          boxShadow: "md",
          bg: "gray.50",
          borderColor: "gray.400"
        }}
        _active={{ transform: "translateY(0)", bg: "gray.100" }}
        onClick={createModal.onOpen}
      >
        Yeni Ekle
      </Button>
    </HStack>
  );

  return (
    <Box mt={12}>
      {/* Üst filtre ve aksiyonlar */}
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="bold" color="white">
          Müşteriler
        </Text>
        {filterControls}
      </Flex>

      {/* Hata ve loading */}
      {error && (
        <Box
          bg="red.50"
          border="1px solid"
          borderColor="red.100"
          p={3}
          borderRadius="md"
          mb={3}
        >
          <Text fontSize="sm" color="red.600">
            {error}
          </Text>
        </Box>
      )}
      {loading && (
        <Flex justify="center" align="center" py={6}>
          <Spinner />
        </Flex>
      )}

      {/* Kart görünümü */}
      <Flex direction="column" gap={3}>
        {tableData.map((row) => {
          const isExpanded = expandedId === row.id;
          const raw = row.raw || {};
          return (
            <Box
              key={row.id}
              bg="white"
              borderRadius="2xl"
              p={4}
              boxShadow="md"
              border="1px solid"
              borderColor={isExpanded ? "blue.100" : "gray.100"}
              cursor="pointer"
              onClick={() =>
                setExpandedId((prev) => (prev === row.id ? null : row.id))
              }
              onContextMenu={(e) => handleRowContextMenu(e, row)}
            >
              {/* Özet satırı */}
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="md" fontWeight="bold">
                    {row.firma_adi || "Firma adı yok"}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {row.telefon} • {row.cozum_turu}
                  </Text>
                </Box>
                <HStack spacing={4} align="center">
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">
                      Aşama
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {row.arama_asamasi}. dönüş
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">
                      Sonraki Arama
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {row.sonraki_arama}
                    </Text>
                  </Box>
                  <Box>{row.uyari}</Box>
                </HStack>
              </Flex>

              {/* Detay kısmı */}
              {isExpanded && (
                <Box mt={4}>
                  <Flex justify="space-between" mb={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Arama Tarihi
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {row.arama_tarihi}
                      </Text>
                    </Box>
                    <Box textAlign="right">
                      <Text fontSize="xs" color="gray.500">
                        Süreç
                      </Text>
                      <Text fontSize="sm" fontWeight="bold">
                        {row.surec_durumu}
                      </Text>
                    </Box>
                  </Flex>

                  <HStack spacing={2} mb={3}>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        openStageModal(row);
                      }}
                    >
                      Dönüş Ekle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="teal"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSmsModal(row);
                      }}
                    >
                      SMS Gönder
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="purple"
                      onClick={(e) => {
                        e.stopPropagation();
                        openTeklifModal(row);
                      }}
                    >
                      PDF Teklif Gönder
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(row);
                      }}
                    >
                      Bilgileri Düzenle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEndProcess(row);
                      }}
                    >
                      Süreç Sonlandır
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        openHistoryModal(row);
                      }}
                    >
                      Dönüş Geçmişi
                    </Button>
                  </HStack>

                  <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Son Dönüş Notu
                    </Text>
                    <Box
                      bg="gray.50"
                      borderRadius="md"
                      p={3}
                      maxH="160px"
                      overflow="auto"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {raw.son_donus_notu ||
                          raw.notlar ||
                          "Henüz dönüş notu girilmemiş."}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Flex>

      <PotansiyelMusteriContextMenu
        position={contextMenuPos}
        data={selectedRow}
        onClose={closeContextMenu}
        onEdit={handleEdit}
        onEndProcess={handleEndProcess}
        onStageChange={openStageModal}
        onShowHistory={openHistoryModal}
      />

      {/* Yeni Potansiyel Müşteri Modalı */}
      <Modal isOpen={createModal.isOpen} onClose={createModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Yeni Potansiyel Müşteri</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Firma Adı</FormLabel>
              <Input
                size="sm"
                value={newLead.firma_adi}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, firma_adi: e.target.value }))
                }
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm">Telefon</FormLabel>
              <Input
                size="sm"
                placeholder="05XXXXXXXXX"
                value={newLead.telefon}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, telefon: e.target.value }))
                }
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Başında 0 olacak şekilde 11 hane girin (ör: 0532xxxxxxx).
              </FormHelperText>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">E-posta</FormLabel>
              <Input
                size="sm"
                type="email"
                value={newLead.email}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Araç Sayısı</FormLabel>
              <Input
                size="sm"
                type="number"
                min="0"
                value={newLead.arac_sayisi}
                onChange={(e) =>
                  setNewLead((prev) => ({
                    ...prev,
                    arac_sayisi: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Çözüm Türü</FormLabel>
              <Input
                size="sm"
                placeholder="Örn: Araç Takip, Filo Yönetimi..."
                value={newLead.cozum_turu}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, cozum_turu: e.target.value }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Not</FormLabel>
              <Textarea
                rows={3}
                size="sm"
                value={newLead.notlar}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, notlar: e.target.value }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={createModal.onClose}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              isLoading={saving}
              onClick={async () => {
                const tel = newLead.telefon.trim();
                if (!tel) {
                  toast({
                    title: "Telefon gerekli",
                    status: "warning",
                  });
                  return;
                }
                if (!/^0\d{10}$/.test(tel)) {
                  toast({
                    title: "Telefon formatı hatalı",
                    description: "Lütfen başında 0 olacak şekilde 11 hane girin. Örn: 0532xxxxxxx",
                    status: "error",
                  });
                  return;
                }
                try {
                  setSaving(true);
                  const payload = {
                    firma_adi: newLead.firma_adi || null,
                    telefon: tel,
                    email: newLead.email || null,
                    arac_sayisi: newLead.arac_sayisi
                      ? Number(newLead.arac_sayisi)
                      : null,
                    cozum_turu: newLead.cozum_turu || null,
                    notlar: newLead.notlar || null,
                  };
                  const res = await api.post(
                    "/api/crm/potansiyel-musteri",
                    payload
                  );
                  const data = await res.json();
                  if (!res.ok) {
                    throw new Error(
                      data?.detail || "Potansiyel müşteri oluşturulamadı."
                    );
                  }
                  toast({
                    title: "Potansiyel müşteri eklendi",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                  });
                  setNewLead({
                    firma_adi: "",
                    telefon: "",
                    email: "",
                    arac_sayisi: "",
                    cozum_turu: "",
                    notlar: "",
                  });
                  createModal.onClose();
                  fetchItems();
                } catch (err) {
                  // Merkezi error handling - Güvenli mesaj göster
                  handleError(err, "Potansiyel müşteri ekleme hatası");
                } finally {
                  setSaving(false);
                }
              }}
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Potansiyel Müşteri Düzenleme Modalı */}
      <Modal isOpen={editModal.isOpen} onClose={editModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Potansiyel Müşteri Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Firma Adı</FormLabel>
              <Input
                size="sm"
                value={editLead.firma_adi}
                onChange={(e) =>
                  setEditLead((prev) => ({ ...prev, firma_adi: e.target.value }))
                }
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm">Telefon</FormLabel>
              <Input
                size="sm"
                value={editLead.telefon}
                onChange={(e) =>
                  setEditLead((prev) => ({ ...prev, telefon: e.target.value }))
                }
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Başında 0 olacak şekilde 11 hane girin (ör: 0532xxxxxxx).
              </FormHelperText>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">E-posta</FormLabel>
              <Input
                size="sm"
                type="email"
                value={editLead.email}
                onChange={(e) =>
                  setEditLead((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Araç Sayısı</FormLabel>
              <Input
                size="sm"
                type="number"
                min="0"
                value={editLead.arac_sayisi}
                onChange={(e) =>
                  setEditLead((prev) => ({
                    ...prev,
                    arac_sayisi: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Çözüm Türü</FormLabel>
              <Input
                size="sm"
                value={editLead.cozum_turu}
                onChange={(e) =>
                  setEditLead((prev) => ({ ...prev, cozum_turu: e.target.value }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Arama Tarihi</FormLabel>
              <Input
                size="sm"
                type="date"
                value={editLead.arama_tarihi || ""}
                onChange={(e) =>
                  setEditLead((prev) => ({
                    ...prev,
                    arama_tarihi: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Not</FormLabel>
              <Textarea
                rows={3}
                size="sm"
                value={editLead.notlar}
                onChange={(e) =>
                  setEditLead((prev) => ({ ...prev, notlar: e.target.value }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={editModal.onClose}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              isLoading={savingEdit}
              onClick={async () => {
                if (!editLead.id) {
                  editModal.onClose();
                  return;
                }
                const tel = editLead.telefon.trim();
                if (!tel) {
                  toast({
                    title: "Telefon gerekli",
                    status: "warning",
                  });
                  return;
                }
                if (!/^0\d{10}$/.test(tel)) {
                  toast({
                    title: "Telefon formatı hatalı",
                    description: "Lütfen başında 0 olacak şekilde 11 hane girin. Örn: 0532xxxxxxx",
                    status: "error",
                  });
                  return;
                }
                try {
                  setSavingEdit(true);
                  const payload = {
                    firma_adi: editLead.firma_adi || null,
                    telefon: tel,
                    email: editLead.email || null,
                    arac_sayisi: editLead.arac_sayisi
                      ? Number(editLead.arac_sayisi)
                      : null,
                    cozum_turu: editLead.cozum_turu || null,
                    notlar: editLead.notlar || null,
                    son_iletisim: editLead.arama_tarihi || null,
                  };
                  const res = await api.patch(
                    `/api/crm/potansiyel-musteri/${editLead.id}`,
                    payload
                  );
                  const data = await res.json();
                  if (!res.ok) {
                    throw new Error(
                      data?.detail || "Potansiyel müşteri güncellenemedi."
                    );
                  }
                  toast({
                    title: "Potansiyel müşteri güncellendi",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                  });
                  editModal.onClose();
                  fetchItems();
                } catch (err) {
                  // Merkezi error handling - Güvenli mesaj göster
                  handleError(err, "Potansiyel müşteri işlemi hatası");
                } finally {
                  setSavingEdit(false);
                }
              }}
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Dönüş (1./2./3.) Modalı */}
      <Modal isOpen={stageModal.isOpen} onClose={closeStageModal} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {stageModal.stage}. Dönüş
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Sonraki Arama Tarihi</FormLabel>
              <Input
                size="sm"
                type="date"
                value={stageModal.tarih}
                onChange={(e) =>
                  setStageModal((prev) => ({ ...prev, tarih: e.target.value }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Not (opsiyonel)</FormLabel>
              <Textarea
                rows={3}
                size="sm"
                value={stageModal.not}
                onChange={(e) =>
                  setStageModal((prev) => ({ ...prev, not: e.target.value }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeStageModal}>
              İptal
            </Button>
            <Button colorScheme="blue" onClick={handleStageSave}>
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Dönüş Geçmişi Modalı */}
      <Modal isOpen={historyModal.isOpen} onClose={closeHistoryModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Dönüş Geçmişi</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {(!historyModal.items || historyModal.items.length === 0) && (
              <Text fontSize="sm" color="gray.500">
                Henüz kayıtlı dönüş notu bulunmuyor.
              </Text>
            )}
            {historyModal.items &&
              historyModal.items.length > 0 && (
                <Box>
                  {historyModal.items.map((item) => (
                    <Box
                      key={item.id}
                      mb={2}
                      p={2}
                      borderRadius="md"
                      bg={
                        item.type === "Dönüş"
                          ? "blue.50"
                          : item.type === "Durum"
                          ? "purple.50"
                          : "gray.50"
                      }
                    >
                      <Text fontSize="xs" fontWeight="bold" color="gray.500">
                        {item.type}
                      </Text>
                      <Text fontSize="sm">{item.label}</Text>
                    </Box>
                  ))}
                </Box>
              )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeHistoryModal}>Kapat</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Süreç Sonlandırma Modalı */}
      <Modal isOpen={endModal.isOpen} onClose={closeEndModal} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Süreç Sonlandır</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              Bu potansiyel müşteri için süreci sonlandırmak üzeresiniz. İsterseniz
              kısa bir not bırakabilirsiniz.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Süreç sonlandırma notu</FormLabel>
              <Textarea
                rows={4}
                size="sm"
                placeholder="Örn: Teklif reddedildi, başka çözüm kullanıyor vb."
                value={endModal.not}
                onChange={(e) =>
                  setEndModal((prev) => ({ ...prev, not: e.target.value }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeEndModal}>
              İptal
            </Button>
            <Button colorScheme="red" onClick={handleEndSave}>
              Süreci Sonlandır
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* SMS Gönderim Modalı */}
      <Modal isOpen={smsModal.isOpen} onClose={closeSmsModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="xl" fontWeight="bold">
            SMS Gönder
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Telefon
              </FormLabel>
              <Input
                size="sm"
                isReadOnly
                value={smsModal.row?.telefon || smsModal.row?.raw?.telefon || ""}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Kayıtlı Mesaj Şablonu
              </FormLabel>
              <Select
                size="sm"
                placeholder={
                  smsTemplatesLoading
                    ? "Şablonlar yükleniyor..."
                    : "Şablon seç (opsiyonel)"
                }
                isDisabled={smsTemplatesLoading || !!smsTemplatesError}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const tmpl = smsTemplates.find((t) => t.id === id);
                  if (tmpl) {
                    setSmsModal((prev) => ({
                      ...prev,
                      message: tmpl.mesaj_metni || "",
                    }));
                  }
                }}
              >
                {smsTemplates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.mesaj_metni.length > 50
                      ? tmpl.mesaj_metni.slice(0, 50) + "..."
                      : tmpl.mesaj_metni}
                  </option>
                ))}
              </Select>
              {smsTemplatesError && (
                <FormHelperText fontSize="xs" color="red.300">
                  {smsTemplatesError}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Mesaj
              </FormLabel>
              <Textarea
                rows={8}
                size="md"
                placeholder="Gönderilecek SMS metnini yazın..."
                value={smsModal.message}
                onChange={(e) =>
                  setSmsModal((prev) => ({ ...prev, message: e.target.value }))
                }
              />
              <FormHelperText fontSize="xs" color="gray.500" mt={1}>
                Mesaj içeriği NetGSM tarafından uzunluğa göre parçalanabilir. Lütfen 160
                karakter bloklarını dikkate alarak yazın.
              </FormHelperText>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeSmsModal}>
              İptal
            </Button>
            <Button colorScheme="teal" onClick={handleSmsSave}>
              Gönder
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* PDF Teklif Gönderim Modalı */}
      <Modal isOpen={teklifModal.isOpen} onClose={closeTeklifModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="xl" fontWeight="bold">
            PDF Teklif Gönder
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl mb={3}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Müşteri Adı
              </FormLabel>
              <Input
                size="sm"
                value={teklifModal.musteri_adi}
                onChange={(e) =>
                  setTeklifModal((prev) => ({
                    ...prev,
                    musteri_adi: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                E-posta
              </FormLabel>
              <Input
                size="sm"
                type="email"
                value={teklifModal.email}
                onChange={(e) =>
                  setTeklifModal((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Boş bırakılırsa kayıttaki e-posta adresi kullanılır.
              </FormHelperText>
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Toplam Fiyat
              </FormLabel>
              <Input
                size="sm"
                type="number"
                min="0"
                step="0.01"
                value={teklifModal.fiyat}
                onChange={(e) =>
                  setTeklifModal((prev) => ({
                    ...prev,
                    fiyat: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Talep İçeriği / Not
              </FormLabel>
              <Textarea
                size="sm"
                placeholder="Teklif mailine eklenecek talep veya not"
                value={teklifModal.talep_icerigi}
                onChange={(e) =>
                  setTeklifModal((prev) => ({
                    ...prev,
                    talep_icerigi: e.target.value,
                  }))
                }
              />
            </FormControl>
            <Box mt={4}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Teklif Kapsamı (Kalemler)
              </FormLabel>
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
              >
                {teklifModal.kalemler.map((kalem, index) => (
                  <HStack key={index} spacing={2} mb={2} align="flex-start">
                    <Box flex="2">
                      <FormLabel fontSize="xs" mb={1}>
                        Ürün / Hizmet
                      </FormLabel>
                      <Input
                        size="sm"
                        value={kalem.urun_hizmet}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTeklifModal((prev) => {
                            const copy = [...prev.kalemler];
                            copy[index] = { ...copy[index], urun_hizmet: value };
                            return { ...prev, kalemler: copy };
                          });
                        }}
                      />
                    </Box>
                    <Box flex="0.6">
                      <FormLabel fontSize="xs" mb={1}>
                        Adet
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        min="1"
                        value={kalem.adet}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTeklifModal((prev) => {
                            const copy = [...prev.kalemler];
                            copy[index] = { ...copy[index], adet: value };
                            return { ...prev, kalemler: copy };
                          });
                        }}
                      />
                    </Box>
                    <Box flex="1">
                      <FormLabel fontSize="xs" mb={1}>
                        Birim Fiyat
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={kalem.birim_fiyat}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTeklifModal((prev) => {
                            const copy = [...prev.kalemler];
                            copy[index] = { ...copy[index], birim_fiyat: value };
                            return { ...prev, kalemler: copy };
                          });
                        }}
                      />
                    </Box>
                    <Box flex="1">
                      <FormLabel fontSize="xs" mb={1}>
                        Toplam Fiyat (opsiyonel)
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={kalem.toplam_fiyat}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTeklifModal((prev) => {
                            const copy = [...prev.kalemler];
                            copy[index] = { ...copy[index], toplam_fiyat: value };
                            return { ...prev, kalemler: copy };
                          });
                        }}
                      />
                    </Box>
                  </HStack>
                ))}
                <Button
                  mt={2}
                  size="xs"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() =>
                    setTeklifModal((prev) => ({
                      ...prev,
                      kalemler: [
                        ...prev.kalemler,
                        {
                          urun_hizmet: "",
                          adet: 1,
                          birim_fiyat: "",
                          toplam_fiyat: "",
                        },
                      ],
                    }))
                  }
                >
                  + İkinci Kalem Ekle
                </Button>
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeTeklifModal}>
              İptal
            </Button>
            <Button colorScheme="purple" onClick={handleTeklifSave}>
              Gönder
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

